import React, { useEffect } from "react";
import { RadioGroup, RadioButton } from "../../components/data_entry/radio";
import QuestionLabel from "./components/question_label";
import { QuestionProps } from "../survey";
import SelectQuestionWrapper from "../../components/base_ui/higher_order_components/questions/select_question_wrapper";

export interface Answer {
  selectedOption: string;
  selectedFollowUpOption?: string;
}

export interface Customizations {
  prompt: string;
  options: string[];
  description?: string;
  followUp?: {
    whenOptionSelected: string;
    prompt: string;
    description?: string;
    options: string[];
    qid?: string;
  };
}

export default function SurveyQuestionSelectWithSelectFollowUp({
  customizations,
  currentAnswer,
  updateAnswer,
  setAbleToSubmit,
  showValidation,
  showRefreshedComponents = true,
}: QuestionProps<Answer, Customizations>) {
  const label = customizations.prompt;
  const options = customizations.options;
  const followUp = customizations.followUp;
  const description = customizations.description;

  const selectedOption = currentAnswer ? currentAnswer.selectedOption : undefined;
  const selectedFollowUpOption = currentAnswer ? currentAnswer.selectedFollowUpOption : undefined;

  const optionIsValid = (currentAnswer?.selectedOption?.length || 0) > 0;
  const followUpOptionIsTriggered = followUp && selectedOption === followUp.whenOptionSelected;
  const followUpOptionIsValid = (currentAnswer?.selectedFollowUpOption?.length || 0) > 0;
  const answerIsValid = optionIsValid && (followUpOptionIsTriggered ? followUpOptionIsValid : true);

  useEffect(() => {
    setAbleToSubmit(!!answerIsValid);
  }, [answerIsValid, setAbleToSubmit]);

  const requiredErrorMessage = "Please complete the required field.";

  const deprecatedSelectWithSelectFollowUp = (
    <div className="multi-select">
      <QuestionLabel label={label} description={description} />
      <RadioGroup
        name="selectQuestion"
        value={selectedOption}
        onChange={(newValue) => updateAnswer({ selectedOption: newValue })}
      >
        {options.map((option) => (
          <RadioButton data-test="select-option" key={option} label={option} value={option} />
        ))}
      </RadioGroup>
      {followUp && selectedOption === followUp.whenOptionSelected && (
        <div>
          <QuestionLabel label={followUp.prompt} description={followUp.description} />
          <RadioGroup
            name="selectQuestion"
            value={selectedFollowUpOption}
            onChange={(followUpValue) =>
              updateAnswer({
                selectedOption: selectedOption,
                selectedFollowUpOption: followUpValue,
              })
            }
          >
            {followUp.options.map((option) => (
              <RadioButton
                data-test="follow-up-select-option"
                key={option}
                label={option}
                value={option}
              />
            ))}
          </RadioGroup>
        </div>
      )}
    </div>
  );

  const selectWithSelectFollowUp = (
    <>
      <SelectQuestionWrapper
        label={label}
        secondaryText={description}
        optional={false}
        options={options.map((option) => ({ id: option, label: option }))}
        value={{ id: selectedOption, label: selectedOption }}
        onChange={(newAnswer) => updateAnswer({ selectedOption: newAnswer.id as string })}
        // SelectQuestionWrapper only handles follow-ups of type Input
        // And SelectWithSelectFollowUp is only used in a single place
        // So this implementation is doing the bare minimum to refresh the UI
        // Without making the follow-up component logic more flexible
        showFollowUp={false}
        errorMessage={!optionIsValid && showValidation ? requiredErrorMessage : undefined}
        dataTest="select-with-select-follow-up"
      />
      {followUpOptionIsTriggered && (
        <SelectQuestionWrapper
          label={followUp.prompt}
          secondaryText={followUp.description}
          optional={false}
          options={followUp.options.map((option) => ({ id: option, label: option }))}
          value={{ id: selectedFollowUpOption, label: selectedFollowUpOption }}
          onChange={(followUpValue) =>
            updateAnswer({
              selectedOption: selectedOption,
              selectedFollowUpOption: followUpValue.id as string,
            })
          }
          errorMessage={!followUpOptionIsValid && showValidation ? requiredErrorMessage : undefined}
          data-test="follow-up-select-option"
        />
      )}
    </>
  );

  return showRefreshedComponents ? selectWithSelectFollowUp : deprecatedSelectWithSelectFollowUp;
}
